import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { isChatMuted, WS_EVENTS } from '@twinden/shared';
import {
  alertSettingsAtom,
  conversationsByFamilyAtom,
  currentUserAtom,
  openConversationIdAtom,
  pushPreferencesAtom,
} from '../store';
import { api } from '../lib/api';
import { isPushSubscribedHere, refreshPushSubscribed } from '../lib/pwa';
import {
  MESSAGE_VIBRATION,
  playMessageSound,
  setupAudioUnlock,
  startRingback,
  startRingtone,
  stopVibration,
  vibrate,
} from '../lib/sounds';
import { useWebSocket } from './useWebSocket';
import { useCall } from './useCall';
import { clearNotificationsOnOpen } from '../lib/notifications';
import { keptArchivedIn } from '../lib/chatSettings';

// A burst of messages makes one sound, not a drumroll.
const MESSAGE_SOUND_GAP_MS = 1500;

interface IncomingMessage {
  conversationId: string;
  familyId?: string | null;
  type?: string;
  sender?: { id: string } | null;
  mentions?: string[];
}

// Closes the OS notification for a call once it stops ringing, so a phone
// doesn't keep showing "Incoming call" after it was answered elsewhere.
async function closeCallNotification(callId: string) {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    const notifications = await registration?.getNotifications({
      tag: `call:${callId}`,
    });
    notifications?.forEach((notification) => notification.close());
  } catch {
    // No service worker (dev, unsupported browser): nothing to close.
  }
}

// In-app sound and vibration for new messages and calls (issue #3). Pushes
// cover the app being closed or in the background; this covers it being open.
export function useAlerts() {
  const settings = useAtomValue(alertSettingsAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const openConversationId = useAtomValue(openConversationIdAtom);
  const [preferences, setPreferences] = useAtom(pushPreferencesAtom);
  const chatLists = useAtomValue(conversationsByFamilyAtom);
  const { onEvent } = useWebSocket();
  const { state: call } = useCall();

  useEffect(() => {
    void refreshPushSubscribed();
  }, []);

  // The WS handler is registered once; it reads the latest values from here.
  const latest = useRef({
    settings,
    currentUser,
    openConversationId,
    preferences,
    chatLists,
  });
  latest.current = {
    settings,
    currentUser,
    openConversationId,
    preferences,
    chatLists,
  };
  const lastSoundAtRef = useRef(0);

  useEffect(() => setupAudioUnlock(), []);

  // Mutes live on the server; load them once for the whole app.
  useEffect(() => {
    if (preferences) return;
    api
      .getPushPreferences()
      .then((response) => {
        if (response.success) setPreferences(response.data);
      })
      .catch(() => undefined);
  }, [preferences, setPreferences]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.MESSAGE_RECEIVED) return;
        const message = payload as IncomingMessage;
        const {
          settings,
          currentUser,
          openConversationId,
          preferences,
          chatLists,
        } = latest.current;

        if (message.sender?.id === currentUser?.id) return;
        // Call summaries: the ringtone already did the job.
        if (message.type === 'system') return;
        // A muted chat stays quiet unless it mentions you (M22, D27).
        const mentionsMe = Boolean(
          currentUser && message.mentions?.includes(currentUser.id)
        );
        // A timed mute ends by itself (D233).
        if (!mentionsMe && isChatMuted(preferences, message.conversationId)) {
          return;
        }
        // A chat kept archived is as quiet as a muted one (D306).
        if (!mentionsMe && keptArchivedIn(chatLists, message.conversationId)) {
          return;
        }
        if (
          message.familyId &&
          preferences?.mutedFamilyIds.includes(message.familyId)
        ) {
          return;
        }

        const visible = document.visibilityState === 'visible';
        // Already reading it.
        if (
          visible &&
          document.hasFocus() &&
          openConversationId === message.conversationId
        ) {
          return;
        }
        // The push notification makes the sound: in the background, and in
        // front too once this device has notifications on, since the server
        // only skips the chat you're looking at (WS_EVENTS.VIEWING).
        const notificationsOn =
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted';
        if (!visible && notificationsOn) return;
        if (
          notificationsOn &&
          isPushSubscribedHere() &&
          preferences?.messages !== false
        ) {
          return;
        }

        const now = Date.now();
        if (now - lastSoundAtRef.current < MESSAGE_SOUND_GAP_MS) return;
        lastSoundAtRef.current = now;

        if (settings.messageSound) playMessageSound();
        if (settings.vibration) vibrate(MESSAGE_VIBRATION);
      }),
    [onEvent]
  );

  // Ring while a call is ringing: a ringtone for incoming calls, and the
  // ringback for our own call once someone's phone is ringing (D278) —
  // silence means it hasn't reached them yet.
  const ringing =
    call.status !== 'ringing'
      ? null
      : call.direction === 'incoming' || call.ringing.length > 0
        ? call.direction
        : null;
  const ringingCallId = call.status === 'ringing' ? call.callId : null;
  useEffect(() => {
    if (!ringing || !ringingCallId) return;

    const stop =
      ringing === 'incoming'
        ? settings.callRingtone || settings.vibration
          ? startRingtone({
              sound: settings.callRingtone,
              vibrate: settings.vibration,
            })
          : () => undefined
        : settings.callRingtone
          ? startRingback()
          : () => undefined;

    return () => {
      stop();
      stopVibration();
      if (ringing === 'incoming') void closeCallNotification(ringingCallId);
    };
  }, [ringing, ringingCallId, settings.callRingtone, settings.vibration]);

  // Opening Twinden (or coming back to it) clears its notifications, except a
  // call still ringing here (2026-10-24).
  const ringingRef = useRef(ringingCallId);
  ringingRef.current = ringing === 'incoming' ? ringingCallId : null;
  useEffect(() => {
    const clear = () => {
      if (document.visibilityState === 'visible') {
        void clearNotificationsOnOpen(ringingRef.current);
      }
    };
    clear();
    document.addEventListener('visibilitychange', clear);
    return () => document.removeEventListener('visibilitychange', clear);
  }, []);
}
